import Axios from "axios";

const apiURL = /myrace.info/.test(window.location.hostname)
  ? "https://photo-api.myrace.info/rpc"
  : /kadr.app/.test(window.location.hostname)
  ? "https://api.kadr.app/rpc"
  : "https://api.portreed.com/rpc";

const axios = Axios.create({
  baseURL: apiURL,
  withCredentials: true,
});

const unauthorizedListeners = new Set();

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error.config || {};
    // Invalid OTPs are login-form errors, not expired uploader sessions.
    if (
      error.response &&
      error.response.status === 401 &&
      !config.skipSessionExpiry &&
      config.url !== "/auth/redeemOtp" &&
      config.url !== "/auth/sendOtp"
    ) {
      unauthorizedListeners.forEach((listener) => listener());
    }
    return Promise.reject(error);
  }
);

function getErrorMessage(error, fallbackMessage = "Request failed") {
  const responseData =
    error && error.response && error.response.data ? error.response.data : null;

  return (
    (typeof responseData === "string" && responseData) ||
    (responseData && (responseData.error || responseData.message)) ||
    (error && error.message) ||
    fallbackMessage
  );
}

export default {
  onUnauthorized(listener) {
    unauthorizedListeners.add(listener);
    return () => unauthorizedListeners.delete(listener);
  },

  async getAlbumInfo(albumId) {
    const response = await axios.get("/album/getSettings", {
      params: { albumId },
    });

    return response.data
      ? {
          id: response.data.id,
          name: response.data.name,
          caption: response.data.caption,
          sealed: response.data.sealed,
        }
      : null;
  },

  async authenticatedUser() {
    return (await axios.get("/auth/me")).data;
  },

  async endSession() {
    await axios.post("/auth/endSession");
  },

  async sendOTP(email) {
    return await axios.post("/auth/sendOtp", { email });
  },

  async redeemOTP(email, code) {
    const response = await axios.post("/auth/redeemOtp", { email, code });
    try {
      // Check that the browser retained the session cookie before entering
      // the uploader; redeemOtp's response alone cannot establish this.
      const verified = await axios.get("/auth/me", { skipSessionExpiry: true });
      const user = verified.data;
      if (!user || !user.id || user.id !== response.data.item.id) {
        throw new Error("Session does not match the login");
      }
      return user;
    } catch (error) {
      const verificationError = new Error("Could not verify the login session");
      verificationError.code = "SESSION_VERIFICATION_FAILED";
      throw verificationError;
    }
  },

  async createUpload(matterId, itemsCount) {
    try {
      const response = await axios.post("/upload/start", {
        albumId: matterId,
        itemsCount,
      });

      if (response.data && response.data.error) {
        throw new Error(response.data.error);
      }

      return response.data;
    } catch (error) {
      error.message = getErrorMessage(error);
      throw error;
    }
  },

  async getS3Credentials(uploadId) {
    return (await axios.post("/upload/getCredentials", { uploadId })).data;
  },

  async finishUpload(uploadId) {
    return (await axios.post("/upload/startProcessing", { uploadId })).data;
  },

  async getAlbumItemsStatus(albumId) {
    return (
      await axios.get("/upload/getAlbumItemsStatus", {
        params: { albumId },
      })
    ).data;
  },
};
